import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, UploadCloud } from "lucide-react";
import { api, formatApiErrorDetail, mediaUrl } from "../lib/api";

export async function uploadMedia(file, meta = {}) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("alt", meta.alt || "");
  fd.append("caption", meta.caption || "");
  fd.append("credit", meta.credit || "");
  const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export default function MediaPicker({ open, onClose, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/media?limit=100")
      .then((r) => setItems(r.data.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const onFiles = async (files) => {
    setError("");
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (!f.type.startsWith("image/")) {
          setError(`"${f.name}" bukan berkas gambar.`);
          continue;
        }
        if (f.size > 8 * 1024 * 1024) {
          setError(`"${f.name}" terlalu besar (maks. 8 MB).`);
          continue;
        }
        await uploadMedia(f);
      }
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Pilih media" onClick={onClose}>
      <div className="bg-ivory border border-line rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-line">
          <h2 className="font-serif text-xl text-ink">Pustaka Media</h2>
          <button onClick={onClose} aria-label="Tutup" data-testid="media-picker-close" className="w-10 h-10 rounded-full hover:bg-surface-muted flex items-center justify-center">×</button>
        </div>
        <div className="p-5 overflow-y-auto">
          <label
            data-testid="media-picker-upload"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
            }}
            className="block border-2 border-dashed border-line rounded-lg p-8 text-center cursor-pointer hover:border-enji transition-colors"
          >
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            {uploading ? (
              <span className="inline-flex items-center gap-2 text-sub text-sm"><Loader2 size={16} className="animate-spin" /> Mengunggah…</span>
            ) : (
              <span className="inline-flex items-center gap-2 text-sub text-sm"><UploadCloud size={18} /> Tarik gambar ke sini atau klik untuk mengunggah (maks. 8 MB)</span>
            )}
          </label>
          {error && <p role="alert" className="text-sm text-enji mt-3">{error}</p>}
          {loading ? (
            <p className="text-sub text-sm mt-6">Memuat…</p>
          ) : items.length === 0 ? (
            <p className="text-faint text-sm mt-6">Belum ada gambar. Unggah foto pertama Anda.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mt-5">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`media-picker-item-${m.id}`}
                  onClick={() => onPick(m)}
                  className="rounded-md overflow-hidden border border-line bg-surface-muted hover:border-enji transition-colors"
                  style={{ aspectRatio: "1" }}
                >
                  <img src={mediaUrl(m.id)} alt={m.alt || m.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

